import { Suspense, lazy } from 'react'
import { useNavigate } from 'react-router-dom'

import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Delete } from '@factorialco/f0-react/icons/app'
import { Pencil } from '@factorialco/f0-react/icons/app'
import { SaleInvoicesDetailPageRouteFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { formattedAmount } from 'modules/banking/lib/paymentHelpers'
import { useArchiveSaleInvoiceDialog } from 'modules/finance/hooks/sale_invoices/useArchiveSaleInvoiceDialog'
import { contactFullName } from 'modules/finance/lib/contactsUtils'
import {
  mapDocumentTypeText,
  mapSaleInvoiceStatusText,
  saleInvoiceStatusTagType,
} from 'modules/finance/lib/invoices'

const AlertIssuedWithErrors = lazy(
  () => import('modules/finance/components/Invoices/SaleInvoiceDetail/AlertIssuedWithErrors')
)

interface Props {
  saleInvoice?: SaleInvoicesDetailPageRouteFragment | null
  humanReadablePreview?: string | null
  projectId?: number | null
}

export const SaleInvoiceHeader = ({
  saleInvoice,
  projectId = null,
  humanReadablePreview = null,
}: Props) => {
  const { access } = useCurrentSession()
  const { showArchiveSaleInvoiceDialog } = useArchiveSaleInvoiceDialog()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const locale = access.locale

  if (!saleInvoice) {
    return
  }

  const saleInvoiceTitle =
    saleInvoice && saleInvoice.documentNumber
      ? saleInvoice.documentNumber
      : humanReadablePreview ?? '-'

  const canCreateCreditNote = !!saleInvoice.validatedAt && !saleInvoice.childFinancialDocument
  const canEditOrDelete = !saleInvoice.validatedAt && !saleInvoice.registerSaleAt

  const amountFormatted =
    saleInvoice.totalAmountCents && saleInvoice.currency
      ? formattedAmount({
          amountCents: Number(saleInvoice.totalAmountCents),
          currency: saleInvoice.currency,
          locale,
        })
      : '-'

  const documentTypeText = mapDocumentTypeText(saleInvoice.documentType)

  const editDraftPath = projectId
    ? navigation.projects.id.details.sale_invoices.detail.form.fullPath({
        id: projectId,
        invoiceId: saleInvoice.id,
      })
    : navigation.finance.sales.salesInvoices.detail.form.fullPath({
        invoiceId: saleInvoice.id,
      })

  const createCreditNotePath = projectId
    ? navigation.projects.id.details.sale_invoices.detail.form.fullPath({
        id: projectId,
        invoiceId: saleInvoice.id,
      })
    : navigation.finance.sales.salesInvoices.detail.form.fullPath({
        invoiceId: saleInvoice.id,
      })

  let primaryAction = undefined
  if (canEditOrDelete) {
    primaryAction = {
      label: i18n.t('finance.sale_invoices.datatable.actions.edit_draft'),
      icon: Pencil,
      onClick: () => {
        navigate(editDraftPath)
      },
    }
  }

  if (canCreateCreditNote) {
    primaryAction = {
      label: i18n.t('finance.sale_invoices.datatable.actions.create_credit_note'),
      icon: Pencil,
      onClick: () => {
        navigate(createCreditNotePath + '?createCreditNote=true')
      },
    }
  }

  const otherActions = canEditOrDelete
    ? [
        {
          label: i18n.t('finance.sale_invoices.datatable.actions.delete_draft'),
          icon: Delete,
          critical: true,
          onClick: () => {
            showArchiveSaleInvoiceDialog({
              invoiceId: saleInvoice.id,
              callback: () => {
                navigate(-1)
              },
            })
          },
        },
      ]
    : undefined

  return (
    <>
      <ResourceHeader
        title={saleInvoiceTitle}
        description={documentTypeText}
        status={{
          label: i18n.t('finance.sale_invoices.detail.header.status'),
          text: mapSaleInvoiceStatusText(!!saleInvoice.validatedAt, !!saleInvoice.registerSaleAt),
          variant: saleInvoiceStatusTagType(
            !!saleInvoice.validatedAt,
            !!saleInvoice.registerSaleAt
          ),
        }}
        metadata={[
          {
            label: i18n.t('finance.sale_invoices.detail.header.billed_to'),
            value: {
              type: 'text',
              content: saleInvoice.recipientName || contactFullName(saleInvoice.vendor),
            },
          },
          {
            label: i18n.t('finance.sale_invoices.detail.header.amount'),
            value: {
              type: 'text',
              content: amountFormatted,
            },
          },
        ]}
        primaryAction={primaryAction}
        otherActions={otherActions}
      />
      <Suspense fallback={null}>
        <AlertIssuedWithErrors
          financialDocumentEvents={saleInvoice.financialDocumentEvents}
          hasCreditNote={!!saleInvoice.childFinancialDocument}
          validated={!!saleInvoice.validatedAt}
        />
      </Suspense>
    </>
  )
}
